import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import {
  useGetInstructorCoursesQuery,
  useDeleteCourseMutation,
} from '../../api/apiSlice';
import Spinner from '../../components/common/Spinner';
import { toast } from 'react-hot-toast';
import { COURSE_COVER_FALLBACK_SM, setFallback } from '../../lib/placeholders';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { computePricing, formatKzt } from '../../lib/coursePricing';

// Список курсов инструктора, разбитый на табы (Активные / Неактивные / На модерации).
//
// Раньше тут была отдельная модалка «Изменить цену» с полями price+discountPrice —
// это дублировало CourseDetailsForm в редакторе курса и приводило к рассинхрону
// (юзер менял цену в модалке без discount%, потом в редакторе видел совсем другое).
// Теперь единственная кнопка действия — «Редактировать», ведёт в полный редактор,
// где цена/скидка/% живут в одном месте с live-preview итоговой цены.

const TABS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
};

const MyCoursesPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TABS.ACTIVE);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const navigate = useNavigate();

  const { data: courses = [], isLoading, error } = useGetInstructorCoursesQuery();
  const [deleteCourse, { isLoading: isDeleting }] = useDeleteCourseMutation();

  if (isLoading) return <Spinner />;

  if (error) {
    return (
      <div className="text-red-500 p-6">
        {t('instructorMyCourses.loadError', { message: error?.data?.message || t('instructorMyCourses.loadErrorFallback') })}
      </div>
    );
  }

  // Разделение курсов по статусам.
  const activeCourses = courses.filter((c) =>
    ['published', 'active'].includes(c.status)
  );
  const inactiveCourses = courses.filter((c) =>
    ['rejected', 'archived', 'inactive', 'draft'].includes(c.status)
  );
  const pendingCourses = courses.filter((c) =>
    ['pending', 'pending_review'].includes(c.status)
  );

  const handleEditCourse = (course) => {
    navigate(`/dashboard/instructor/create-course/${course._id}/edit`);
  };

  // Превью курса глазами студента — открываем плеер /learn/:id в новой
  // вкладке. Бэкенд даёт owner-bypass: автор курса видит все видео и материалы
  // без enrollment'а, gate-квизы тоже разблокированы. Прогресс не пишется.
  const handleViewCourse = (course) => {
    window.open(`/learn/${course._id}`, '_blank', 'noopener,noreferrer');
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteCourse(pendingDeleteId).unwrap();
      toast.success(t('instructorMyCourses.deleteSuccess'));
    } catch (err) {
      toast.error(err?.data?.message || t('instructorMyCourses.deleteError'));
    } finally {
      setPendingDeleteId(null);
    }
  };

  const currentCourses =
    activeTab === TABS.ACTIVE
      ? activeCourses
      : activeTab === TABS.INACTIVE
      ? inactiveCourses
      : pendingCourses;

  const courseToDelete = courses.find((c) => c._id === pendingDeleteId);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100">{t('instructorMyCourses.title')}</h1>

      <div className="flex border-b border-gray-300 dark:border-slate-600 mb-6">
        <TabButton
          label={t('instructorMyCourses.tabActive', { count: activeCourses.length })}
          active={activeTab === TABS.ACTIVE}
          onClick={() => setActiveTab(TABS.ACTIVE)}
        />
        <TabButton
          label={t('instructorMyCourses.tabInactive', { count: inactiveCourses.length })}
          active={activeTab === TABS.INACTIVE}
          onClick={() => setActiveTab(TABS.INACTIVE)}
        />
        <TabButton
          label={t('instructorMyCourses.tabPending', { count: pendingCourses.length })}
          active={activeTab === TABS.PENDING}
          onClick={() => setActiveTab(TABS.PENDING)}
        />
      </div>

      {currentCourses.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 dark:border-slate-700 p-12 text-center text-gray-500 dark:text-gray-400">
          {t('instructorMyCourses.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentCourses.map((course) => (
            <CourseCard
              key={course._id}
              course={course}
              onView={() => handleViewCourse(course)}
              onEdit={() => handleEditCourse(course)}
              onDelete={() => setPendingDeleteId(course._id)}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title={t('instructorMyCourses.confirmDeleteTitle')}
        description={t('instructorMyCourses.confirmDeleteDesc', { title: courseToDelete?.title || '' })}
        confirmLabel={t('instructorMyCourses.confirmDeleteCta')}
        destructive
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

const TabButton = ({ label, active, onClick }) => (
  <button
    className={`px-4 py-2 ${active ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 dark:text-gray-300'}`}
    onClick={onClick}
  >
    {label}
  </button>
);

const CourseCard = ({ course, onView, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation();
  const statusKey = {
    published: 'statusActive',
    active: 'statusActive',
    pending: 'statusPending',
    pending_review: 'statusPending',
    draft: 'statusDraft',
    rejected: 'statusRejected',
    inactive: 'statusInactive',
    archived: 'statusArchived',
  }[course.status];
  const statusLabel = statusKey ? t(`instructorMyCourses.${statusKey}`) : course.status;

  // Единый helper (lib/coursePricing) — гарантия что все места показывают
  // цену одинаково: каталог, деталка, превью в редакторе.
  const { basePrice, finalPrice, discountPercent, hasDiscount } = computePricing(course);

  return (
    <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow border border-gray-200 dark:border-slate-700">
      <div className="relative">
        <img
          src={course.coverImageUrl || COURSE_COVER_FALLBACK_SM}
          alt={course.title}
          loading="lazy"
          onError={setFallback(COURSE_COVER_FALLBACK_SM)}
          className="w-full h-40 object-cover rounded-lg mb-4"
        />
        <div className="absolute top-2 right-2 bg-gray-800 text-white px-2 py-1 rounded text-xs">
          {statusLabel}
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 break-words">
        {course.title}
      </h3>

      <p className="text-sm mb-4 tabular-nums">
        {hasDiscount ? (
          <>
            <span className="line-through text-gray-500 mr-2">{formatKzt(basePrice)}</span>
            <span className="text-green-600 font-semibold">{formatKzt(finalPrice)}</span>
            <span className="ml-2 text-xs rounded-full bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 px-2 py-0.5 font-semibold">
              −{discountPercent}%
            </span>
          </>
        ) : (
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatKzt(basePrice)}
          </span>
        )}
      </p>

      <div className="flex gap-2">
        <button
          onClick={onView}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-medium"
          title={t('instructorMyCourses.view', 'Посмотреть')}
        >
          <Eye size={15} />
          <span className="hidden sm:inline">{t('instructorMyCourses.view', 'Посмотреть')}</span>
        </button>
        <button
          onClick={onEdit}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
        >
          <Pencil size={15} />
          {t('instructorMyCourses.edit')}
        </button>

        {course.status === 'draft' && (
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium disabled:opacity-50"
            title={t('instructorMyCourses.delete')}
          >
            <Trash2 size={15} />
            <span className="hidden sm:inline">{t('instructorMyCourses.delete')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default MyCoursesPage;
